import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // MONTH-CLICK v1
import * as dispatchApi from '../services/dispatchAnalysisApi';
import DateInput from './DateInput';

const BORDER = '1px solid var(--color-border)';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAGE = 100;

const fmtDate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
const fmtMonth = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const num = (n) => Number(n || 0).toLocaleString('en-IN');

const th = {
  padding: '6px 8px', fontSize: 'var(--font-size-xs)', color: '#1e293b', fontWeight: 700, textAlign: 'left', background: '#e2e8f0', whiteSpace: 'nowrap',
};
const td = { padding: '6px 8px', fontSize: 'var(--font-size-sm)', borderTop: BORDER };

function Tile({ label, value, sub }) {
  return (
    <div style={{
      flex: '1 1 130px', border: BORDER, borderRadius: 'var(--radius-sm)', padding: '8px 12px', background: '#fff',
    }}
    >
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 8px' }}>{title}</h4>
      {children}
    </div>
  );
}

function RankTable({ rows, nameHeader }) {
  const max = Math.max(1, ...rows.map((r) => r.qty));
  return (
    <div className="table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>{nameHeader}</th>
            <th style={th}>Box</th>
            <th style={th}>%</th>
            <th style={th}>Dispatches</th>
            <th style={{ ...th, width: '30%' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
              <td className="data-figure" style={td}>{num(r.qty)}</td>
              <td className="data-figure" style={td}>{r.pct}%</td>
              <td className="data-figure" style={td}>{r.dispatches}</td>
              <td style={td}>
                <div style={{ height: 8, borderRadius: 4, background: '#e2e8f0' }}>
                  <div style={{ height: 8, borderRadius: 4, width: `${(r.qty / max) * 100}%`, background: 'var(--color-accent)' }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthBars({ months, onPick }) {
  const recent = months.slice(-24);
  const max = Math.max(1, ...recent.map((m) => m.qty));
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 6, height: 150, minWidth: recent.length * 38, paddingTop: 16,
      }}
      >
        {recent.map((m) => (
          <div key={m.month} title={`${fmtMonth(m.month)}: ${num(m.qty)} Box in ${m.dispatches} dispatch(es)`} style={{ flex: '1 0 32px', textAlign: 'center', cursor: onPick ? 'pointer' : 'default' }} onClick={onPick ? () => onPick(m.month) : undefined}>
            <div style={{ fontSize: 10, color: '#1e293b' }}>{num(m.qty)}</div>
            <div style={{
              height: Math.max(3, (m.qty / max) * 100), background: 'var(--color-accent)', borderRadius: '3px 3px 0 0', margin: '0 auto', width: 22,
            }}
            />
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{MONTHS[Number(m.month.slice(5, 7)) - 1]}</div>
            <div style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>{m.month.slice(2, 4)}</div>
          </div>
        ))}
      </div>
      {months.length > 24 && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Last 24 active months shown ({months.length} in total).</div>}
    </div>
  );
}

function csvOf(name, lines) {
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const withCust = lines.some((l) => l.customer);
  const head = [...(withCust ? ['Party'] : []), 'Date', 'Design', 'Size', 'Batch/Shade', 'Box Pack', 'Place', 'Premium', 'Standard', 'Commercial', 'Economy', 'Total Box'];
  const body = lines.map((l) => [...(withCust ? [l.customer] : []), fmtDate(l.date), l.design, l.size, l.batchShade, l.boxPack, l.place, l.pre, l.std, l.com, l.eco, l.qty].map(esc).join(','));
  return [`${esc(name)}`, head.map(esc).join(','), ...body].join('\n');
}

export default function CustomerHistoryModal({ customer, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [dFrom, setDFrom] = useState(customer.from || '');
  const [dTo, setDTo] = useState(customer.to || '');

  useEffect(() => {
    let dead = false;
    setData(null);
    setError(null);
    (customer.kind === 'refParty' ? dispatchApi.getRefPartyHistory(customer.id) : dispatchApi.getCustomerHistory(customer.id))
      .then((d) => { if (!dead) setData(d); })
      .catch((e) => { if (!dead) setError((e && e.message) || 'Could not load history.'); });
    return () => { dead = true; };
  }, [customer.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.lines.filter((l) => {
      if (dFrom && l.date < dFrom) return false;
      if (dTo && l.date > dTo) return false;
      if (!s) return true;
      return [fmtDate(l.date), l.design, l.size, l.batchShade, l.boxPack, l.place, l.customer || ''].join(' ').toLowerCase().includes(s);
    });
  }, [data, q, dFrom, dTo]);

  const totals = useMemo(() => filtered.reduce((t, l) => ({
    pre: t.pre + l.pre, std: t.std + l.std, com: t.com + l.com, eco: t.eco + l.eco, qty: t.qty + l.qty,
  }), {
    pre: 0, std: 0, com: 0, eco: 0, qty: 0,
  }), [filtered]);

  const download = () => {
    const blob = new Blob(['\ufeff' + csvOf(customer.name, filtered)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${customer.name.replace(/[^A-Za-z0-9]+/g, '_')}_dispatch_history.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const s = data && data.summary;
  const isRef = customer.kind === 'refParty';
  return (
    <div
      role="dialog"
      aria-label={`${customer.name} dispatch history`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#f8fafc', borderRadius: 'var(--radius-md, 10px)', width: 'min(1040px, 100%)', padding: 'var(--space-4, 16px)', boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{isRef ? 'Ref. Party' : 'Customer'} — complete dispatch history</div>
            <h3 style={{ margin: 0 }}>{customer.name}</h3>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>✕ Close</button>
        </div>

        {!data && !error && <p style={{ color: 'var(--color-text-secondary)' }}>Loading history…</p>}
        {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

        {data && s.dispatchCount === 0 && <p style={{ color: 'var(--color-text-muted)' }}>No dispatches recorded for this customer.</p>}

        {data && s.dispatchCount > 0 && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <Tile label="Total Box" value={num(s.totalQty)} sub={`avg ${s.avgPerDispatch} per line`} />
              <Tile label="Dispatch lines" value={num(s.dispatchCount)} sub={`${num(s.dispatchDays)} dispatch days`} />
              <Tile label="First dispatch" value={fmtDate(s.firstDate)} />
              <Tile label="Last dispatch" value={fmtDate(s.lastDate)} />
              <Tile label="Active months" value={s.activeMonths} />
              {isRef && <Tile label="Parties" value={num(s.customerCount)} />}
            </div>
            <div style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: '#1e293b' }}>
              Grade-wise Box — Premium <b>{num(s.grades.pre)}</b> · Standard <b>{num(s.grades.std)}</b> · Commercial <b>{num(s.grades.com)}</b> · Economy <b>{num(s.grades.eco)}</b>
            </div>

            <Section title="Year-wise">
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Year</th><th style={th}>Box</th><th style={th}>Dispatch lines</th></tr></thead>
                  <tbody>
                    {data.byYear.map((y) => (
                      <tr key={y.year}>
                        <td style={{ ...td, fontWeight: 600 }}>{y.year}</td>
                        <td className="data-figure" style={td}>{num(y.qty)}</td>
                        <td className="data-figure" style={td}>{y.dispatches}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Month-wise Box">
              <MonthBars
                months={data.byMonth}
                onPick={isRef ? (m) => { onClose(); navigate(`/ref-party-month?month=${m}&party=${customer.id}`); } : undefined}
              />
              {isRef && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>Click a month to see its full details.</div>}
            </Section>
            {isRef && data.topCustomers && (
              <Section title="Party-wise Box (customers of this Ref. Party)"><RankTable rows={data.topCustomers} nameHeader="Party" /></Section>
            )}
            <Section title="Designs taken (most to least)"><RankTable rows={data.topDesigns} nameHeader="Design" /></Section>
            <Section title="Sizes"><RankTable rows={data.topSizes} nameHeader="Size" /></Section>
            <Section title="Where the material went (city, state)"><RankTable rows={data.topPlaces} nameHeader="Place" /></Section>

            <Section title={`All dispatches (${filtered.length}${data.truncated ? '+' : ''})`}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <input
                  type="search"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
                  placeholder="Search date, design, size, batch/shade, box pack, place…"
                  style={{
                    flex: '1 1 240px', padding: '6px 10px', border: BORDER, borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)',
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  From
                  <DateInput
                    value={dFrom}
                    max={dTo || undefined}
                    onChange={(e) => { setDFrom(e.target.value); setShown(PAGE); }}
                    aria-label="History from date"
                    style={{ padding: '5px 8px', border: BORDER, borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  To
                  <DateInput
                    value={dTo}
                    min={dFrom || undefined}
                    onChange={(e) => { setDTo(e.target.value); setShown(PAGE); }}
                    aria-label="History to date"
                    style={{ padding: '5px 8px', border: BORDER, borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
                  />
                </label>
                {(dFrom || dTo) && (
                  <button type="button" onClick={() => { setDFrom(''); setDTo(''); setShown(PAGE); }} style={{ padding: '6px 12px', border: BORDER, borderRadius: 'var(--radius-sm)', background: '#fff', cursor: 'pointer' }}>
                    ✕ Clear dates
                  </button>
                )}
                <button type="button" onClick={download} style={{ padding: '6px 12px', border: BORDER, borderRadius: 'var(--radius-sm)', background: '#fff', cursor: 'pointer' }}>
                  ⬇ Download CSV
                </button>
              </div>
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[...(isRef ? ['Party'] : []), 'Date', 'Design', 'Size', 'Batch/Shade', 'Box Pack', 'Place', 'Prem', 'Std', 'Com', 'Eco', 'Total Box'].map((h) => <th key={h} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, shown).map((l) => (
                      <tr key={l.id}>
                        {isRef && <td style={{ ...td, fontWeight: 600 }}>{l.customer}</td>}
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(l.date)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{l.design}</td>
                        <td style={td}>{l.size}</td>
                        <td style={td}>{l.batchShade}</td>
                        <td style={td}>{l.boxPack}</td>
                        <td style={td}>{l.place}</td>
                        <td className="data-figure" style={td}>{l.pre}</td>
                        <td className="data-figure" style={td}>{l.std}</td>
                        <td className="data-figure" style={td}>{l.com}</td>
                        <td className="data-figure" style={td}>{l.eco}</td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700 }}>{l.qty}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && <tr><td colSpan={isRef ? 12 : 11} style={{ ...td, color: 'var(--color-text-muted)' }}>No dispatch matches this search.</td></tr>}
                    {filtered.length > 0 && (
                      <tr style={{ background: '#e2e8f0' }}>
                        <td colSpan={isRef ? 7 : 6} style={{ ...td, fontWeight: 700, color: '#1e293b', textAlign: 'right', borderTop: '2px solid var(--color-border)' }}>
                          {`Grand Total (${filtered.length} lines${data.truncated && !q.trim() ? ', latest only' : ''})`}
                        </td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>{num(totals.pre)}</td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>{num(totals.std)}</td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>{num(totals.com)}</td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>{num(totals.eco)}</td>
                        <td className="data-figure" style={{ ...td, fontWeight: 700, color: 'var(--color-accent)', borderTop: '2px solid var(--color-border)' }}>{num(totals.qty)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filtered.length > shown && (
                <button type="button" onClick={() => setShown(shown + PAGE)} style={{ marginTop: 8, padding: '6px 12px', border: BORDER, borderRadius: 'var(--radius-sm)', background: '#fff', cursor: 'pointer' }}>
                  Show {Math.min(PAGE, filtered.length - shown)} more ({filtered.length - shown} left)
                </button>
              )}
              {data.truncated && <p style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Only the latest {data.lines.length} dispatch lines are listed; the totals above cover everything.</p>}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
